import fnv1a from '@sindresorhus/fnv1a';
import { orderBy } from 'firebase/firestore';
import { AbstractCollection } from '../../abstract/Collection';
import { AbstractDocument, Identifiable, Timestampable } from '../../abstract/Document';
import { keys } from '../../helpers/keys';
import { type Supplier, Suppliers } from '../Suppliers';
import type { IPriceAI } from './Prices.ai.types';
import type { IPrice } from './Prices.types';
import { getPricingBasis } from '@bridebook/toolbox/src/pricing/ghost-inclusions';
import { groupPricesByYear } from '@bridebook/toolbox/src/supplier/utils/pricing';

@Identifiable
@Timestampable
export class Price extends AbstractDocument<IPrice> {
  readonly collections = {};

  /**
   * Estimate the price based on the provided number of guests.
   * This will adjust the prices for each month and day of the week based on the extra guest costs.
   *
   * @param guests Partial record of guest types and their counts.
   * @param snapshot Optional snapshot to base the estimate on, otherwise fetches current document data.
   * @param options Selected option packages, added on top of this estimate.
   */
  async getEstimate(guests?: Partial<Record<keyof IPrice['guests'], number>>, snapshot?: IPrice, options?: IPrice[]) {
    if (snapshot == null) {
      snapshot = await this.get(true);
    }

    if (guests == null && (options == null || options.length === 0)) {
      return snapshot;
    }

    const result = Prices.estimate(snapshot, guests);

    /**
     * Options are estimated the same way and folded into this package's matrix, so the
     * caller gets one total per date. A date this package isn't sold on stays unpriced.
     */
    for (const option of options ?? []) {
      const estimate = Prices.estimate(option, guests);

      for (const month of keys(result.price)) {
        for (const day of keys(result.price[month])) {
          const value = result.price[month][day];
          const extra = estimate.price[month]?.[day];

          if (value != null && extra != null) {
            result.price[month][day] = value + extra;
          }
        }
      }
    }

    return result;
  }

  /**
   * Generate a non-cryptographic compound hash of critical package data.
   * This is used to quickly compare if two packages are identical in key pricing aspects.
   *
   * @param snapshot Optional snapshot to hash, otherwise fetches current document data.
   * @param friendly If true, will output a more human-friendly compound hash.
   */
  async signature(snapshot?: IPrice | IPriceAI, friendly = false) {
    if (snapshot == null) {
      snapshot = await this.get(true);
    }

    return Prices.signature(snapshot, friendly);
  }
}

export class Prices extends AbstractCollection<Price, IPrice> {
  static path = 'prices';

  constructor(document: Supplier) {
    super(document.collection(Prices.path), Price);
  }

  get supplier() {
    return Suppliers._.getById(this.reference.parent!.id);
  }

  /**
   * Apply the guest costs to a package, adjusting the price of every month and day
   * of the week by what the guests are charged.
   *
   * A base of exactly one marks a package sold per head, so the date price is charged for
   * every guest alongside the per-guest price. Any other base is a package that includes
   * those guests, and only the ones beyond it are charged on top of the date price.
   *
   * @param snapshot Package to estimate.
   * @param guests Partial record of guest types and their counts.
   */
  static estimate(snapshot: IPrice, guests?: Partial<Record<keyof IPrice['guests'], number>>) {
    const result = structuredClone(snapshot);

    // Money charged per guest, on top of the date price.
    let guestCharges = 0;
    // What the date price is multiplied by: one flat charge, or one per head.
    let multiplier = 1;

    const pricingBasis = getPricingBasis(snapshot.included);

    for (const key of keys(guests ?? {})) {
      const base = snapshot.guests[key]?.base;
      const count = guests?.[key] ?? null;

      if (count == null || base == null) {
        continue;
      }

      const perGuest = snapshot.guests[key].price ?? 0;

      if (pricingBasis === 'perHead' && count > 0) {
        // Sold per head: every guest pays the per-guest price and scales the date price.
        guestCharges += count * perGuest;
        multiplier = Math.max(multiplier, count);
      } else if (count > base) {
        // Sold as a package: only the guests beyond the base are charged.
        guestCharges += (count - base) * perGuest;
      }
    }

    for (const month of keys(result.price)) {
      for (const day of keys(result.price[month])) {
        const price = result.price[month][day];

        /**
         * @todo Handle the case where the "hire" price is null, but we still have per-person guest costs to add.
         */
        if (price != null) {
          result.price[month][day] = price * multiplier + guestCharges;
        }
      }
    }

    return result;
  }

  /**
   * Generate a non-cryptographic compound hash of critical package data.
   * This is used to quickly compare if two packages are identical in key pricing aspects.
   *
   * @param snapshot Snapshot to hash.
   * @param friendly If true, will output a more human-friendly compound hash.
   */
  static signature(snapshot: IPrice | IPriceAI, friendly = false) {
    const dates: (number | null)[] = [];

    if (snapshot.active.since == null) {
      dates.push(null);
    } else if (typeof snapshot.active.since === 'string') {
      dates.push(new Date(snapshot.active.since).getTime());
    } else if (snapshot.active.since instanceof Date) {
      dates.push(snapshot.active.since.getTime());
    } else {
      dates.push(snapshot.active.since.toMillis());
    }

    if (snapshot.active.until == null) {
      dates.push(null);
    } else if (typeof snapshot.active.until === 'string') {
      dates.push(new Date(snapshot.active.until).getTime());
    } else if (snapshot.active.until instanceof Date) {
      dates.push(snapshot.active.until.getTime());
    } else {
      dates.push(snapshot.active.until.toMillis());
    }

    const result = [
      friendly === true ? dates.map((value) => (value == null ? '∞' : new Date(value).toISOString().split('T').at(0))) : dates,
      [snapshot.exclusive, snapshot.vat],
    ];

    for (const key of keys(snapshot.guests)) {
      result.push(
        (friendly === true ? [key as any] : []).concat([
          snapshot.guests[key].base,
          snapshot.guests[key].price,
          snapshot.guests[key].range.at(0) ?? null,
          snapshot.guests[key].range.at(1) ?? null,
        ]),
      );
    }

    for (const month of keys(snapshot.price)) {
      result.push((friendly === true ? [month] : []).concat(Object.values(snapshot.price[month])));
    }

    return friendly
      ? result.map((chunk) => chunk.join('|')).join('|')
      : result.map((chunk) => fnv1a(chunk.join('|')).toString(36)).join('|');
  }

  /**
   * Select the pricing variant for the given slug that is active on the given date.
   * Where multiple variants overlap, the one with the narrowest active date range wins.
   *
   * @param prices Packages to resolve against (typically all packages of a supplier).
   * @param slug Stable slug shared by the temporal variants of the package.
   * @param date Date to resolve the variant for.
   */
  static resolveVariant(prices: IPrice[], slug: string, date: Date): IPrice | undefined {
    const time = date.getTime();
    let result: IPrice | undefined;
    let threshold = Infinity;

    for (const price of prices) {
      if (price.slug !== slug) {
        continue;
      }

      const since = price.active.since?.toMillis() ?? -Infinity;
      const until = price.active.until?.toMillis() ?? +Infinity;

      if (time < since || time > until) {
        continue;
      }

      const candidate = until - since;

      if (result == null || candidate < threshold) {
        result = price;
        threshold = candidate;
      }
    }

    return result;
  }

  /**
   * Get all packages that are active the current year and next 3 years, grouped by year.
   * If no packages exist for a specific year, it will fallback to packages available in the closest year.
   */
  async getAll() {
    const prices = Object.values(await this.query([orderBy('order', 'asc')]).get(true));

    return groupPricesByYear(prices);
  }
}
